import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Trash2, Loader2, Shield, Edit3 } from 'lucide-react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { AdminLayout } from './AdminLayout';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

function MemberForm({ onSave, onCancel, loading }) {
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'editor' });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="space-y-4">
      {[
        { key: 'name', label: 'Name', type: 'text', placeholder: 'Max Mustermann' },
        { key: 'email', label: 'E-Mail', type: 'email', placeholder: 'editor@hosting-community.de' },
        { key: 'password', label: 'Passwort', type: 'password', placeholder: 'Min. 8 Zeichen' },
      ].map(({ key, label, type, placeholder }) => (
        <div key={key} className="space-y-1">
          <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">{label}</label>
          <input type={type} value={form[key]} onChange={e => set(key, e.target.value)}
            required placeholder={placeholder} data-testid={`member-form-${key}`}
            className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500" />
        </div>
      ))}

      <div className="space-y-1">
        <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">Rolle</label>
        <div className="grid grid-cols-2 gap-2">
          {[
            { value: 'editor', label: 'Editor', desc: 'Anbieter verwalten' },
            { value: 'admin', label: 'Administrator', desc: 'Voller Zugriff' },
          ].map(({ value, label, desc }) => (
            <label key={value} data-testid={`role-${value}`}
              className={`flex flex-col p-3 rounded-lg border cursor-pointer transition-colors ${
                form.role === value ? 'bg-cyan-500/10 border-cyan-500/30 text-cyan-400' : 'bg-slate-800 border-slate-700 text-slate-400'
              }`}>
              <input type="radio" name="role" value={value} checked={form.role === value}
                onChange={() => set('role', value)} className="sr-only" />
              <span className="text-sm font-semibold">{label}</span>
              <span className="text-xs opacity-70 mt-0.5">{desc}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="flex gap-2 pt-1">
        <button onClick={() => onSave(form)} disabled={!form.name || !form.email || !form.password || loading}
          data-testid="member-form-save"
          className="flex-1 py-2 bg-cyan-500 text-slate-950 font-semibold rounded-lg hover:bg-cyan-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-sm">
          {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          Hinzufügen
        </button>
        <button onClick={onCancel} className="px-4 py-2 bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors text-sm">
          Abbrechen
        </button>
      </div>
    </div>
  );
}

export function AdminTeam() {
  const { authHeaders, API, user: me } = useAdminAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/team`, { headers: authHeaders() });
      setMembers(r.data);
    } finally { setLoading(false); }
  }, [API, authHeaders]);

  useEffect(() => { load(); }, [load]);

  const addMember = async (form) => {
    setError('');
    setSaving(true);
    try {
      await axios.post(`${API}/admin/team`, form, { headers: authHeaders() });
      await load();
      setShowAdd(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Hinzufügen');
    } finally { setSaving(false); }
  };

  const removeMember = async (id) => {
    await axios.delete(`${API}/admin/team/${id}`, { headers: authHeaders() });
    setDeleteConfirm(null);
    await load();
  };

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 md:p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">Team</h1>
            <p className="text-sm text-slate-500 mt-0.5">{members.length} Mitglieder</p>
          </div>
          <button onClick={() => setShowAdd(true)} data-testid="add-member-btn"
            className="flex items-center gap-2 px-4 py-2 bg-cyan-500 text-slate-950 font-semibold rounded-lg hover:bg-cyan-400 transition-colors text-sm">
            <Plus className="w-4 h-4" /> Mitglied hinzufügen
          </button>
        </div>

        {/* Role info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {[
            { icon: Shield, title: 'Administrator', desc: 'Voller Zugriff: Anbieter verwalten, Team verwalten', color: 'cyan' },
            { icon: Edit3, title: 'Editor', desc: 'Anbieter hinzufügen und bearbeiten', color: 'slate' },
          ].map(({ icon: Icon, title, desc, color }) => (
            <div key={title} className={`flex items-start gap-3 p-4 rounded-xl bg-[#0f172a] border ${color === 'cyan' ? 'border-cyan-500/20' : 'border-slate-800'}`}>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${color === 'cyan' ? 'bg-cyan-500/10' : 'bg-slate-800'}`}>
                <Icon className={`w-4 h-4 ${color === 'cyan' ? 'text-cyan-400' : 'text-slate-400'}`} />
              </div>
              <div>
                <p className="text-sm font-semibold text-white">{title}</p>
                <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
              </div>
            </div>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
          </div>
        ) : (
          <div className="bg-[#0f172a] border border-slate-800 rounded-xl overflow-hidden">
            <table className="w-full" data-testid="team-table">
              <thead>
                <tr className="border-b border-slate-800">
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">Mitglied</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">Rolle</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-400 hidden md:table-cell">Erstellt</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-400">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m, i) => (
                  <tr key={m.id || i} data-testid={`member-row-${i}`}
                    className="border-b border-slate-800/60 hover:bg-white/[0.02] transition-colors">
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-white">{m.name} {m.id === me?.id && <span className="text-xs text-slate-500">(du)</span>}</p>
                      <p className="text-xs text-slate-500">{m.email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${
                        m.role === 'admin' ? 'bg-cyan-500/15 text-cyan-400' : 'bg-slate-700 text-slate-400'
                      }`}>
                        {m.role === 'admin' ? 'Administrator' : 'Editor'}
                      </span>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <p className="text-xs text-slate-500">
                        {m.created_at ? new Date(m.created_at).toLocaleDateString('de-DE') : '–'}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {m.id !== me?.id ? (
                        <button onClick={() => setDeleteConfirm(m)} data-testid={`remove-member-${i}`}
                          className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      ) : (
                        <span className="text-xs text-slate-700 px-2">–</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add dialog */}
      <Dialog open={showAdd} onOpenChange={open => { if (!open) { setShowAdd(false); setError(''); } }}>
        <DialogContent className="bg-[#0f172a] border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Teammitglied hinzufügen</DialogTitle>
          </DialogHeader>
          {error && <div className="px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400">{error}</div>}
          <MemberForm onSave={addMember} onCancel={() => { setShowAdd(false); setError(''); }} loading={saving} />
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteConfirm} onOpenChange={open => !open && setDeleteConfirm(null)}>
        <DialogContent className="bg-[#0f172a] border-slate-700 text-white max-w-sm">
          <DialogHeader><DialogTitle>Mitglied entfernen</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-400">
            Soll <span className="text-white font-semibold">{deleteConfirm?.name}</span> wirklich entfernt werden?
          </p>
          <div className="flex gap-2 mt-2">
            <button onClick={() => removeMember(deleteConfirm?.id)} data-testid="confirm-remove-member"
              className="flex-1 py-2 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 text-sm">
              Ja, entfernen
            </button>
            <button onClick={() => setDeleteConfirm(null)}
              className="flex-1 py-2 bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 text-sm">
              Abbrechen
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
